import type { Account, Contact, Conversation, DisappearTimerSec, GroupRecord, MessageEvent, SecurityEvent } from "@veto/domain";
import type { WasmIdentity } from "@veto/crypto-bridge";
import type { RelayPool } from "@veto/transport";
import type { RefObject } from "react";
import type { SettingsTab } from "../screens/SettingsPanel";
import type { InAppBannerPayload, NotificationPrefs, NotificationPermissionState } from "../notifications";
import type { SecureVaultCapabilities } from "../secureVault";
import type { ProfileInput } from "../hooks/useProfileActions";
import type { ChatRowAction } from "../hooks/useConversationChrome";
import { AppShell } from "./AppShell";
import { CreatePollModal } from "./CreatePollModal";
import { ChatLockGate } from "./ChatLockGate";
import { ChatLockPromptModal } from "./ChatLockPromptModal";
import { saveContact, upsertConversation } from "@veto/storage";
import type { MessageHit } from "./ConversationListPane";

export type ChatActions = {
  sendMessage: () => Promise<void>;
  sendImage: (f: File) => Promise<void>;
  sendDocument: (f: File) => Promise<void>;
  sendViewOnceImage: (f: File) => Promise<void>;
  sendSticker: (packId: string, stickerId: string) => Promise<void>;
  sendStickerFile: (f: File) => Promise<void>;
  sendGif: (f: File) => Promise<void>;
  openViewOnce: (m: MessageEvent) => Promise<void>;
  sendVoiceNote: (b: Blob, ms: number) => Promise<void>;
  tryOpenDirectPeer: (opts?: { quiet?: boolean }) => Promise<void>;
  sendReaction: (m: MessageEvent, emoji: string) => Promise<void>;
  deleteForMe: (m: MessageEvent) => Promise<void>;
  deleteForEveryone: (m: MessageEvent) => Promise<void>;
  forwardMessage: (m: MessageEvent) => Promise<void>;
  forwardTo: (m: MessageEvent, targets: Conversation[]) => Promise<void>;
  starMessage: (m: MessageEvent) => Promise<void>;
  pinMessage: (m: MessageEvent) => Promise<void>;
};

export type GroupActions = {
  createGroup: (title: string, memberIds: string[]) => Promise<void>;
  removeGroupMember: (hex: string) => Promise<void>;
  addGroupMembers: (contactIds: string[]) => Promise<void>;
  changeGroupRole: (hex: string, role: "admin" | "member") => Promise<void>;
  renameGroup: (title: string) => Promise<void>;
};

/** Presentational binding — keeps App.tsx under the H1 composition budget. */
export function BoundAppShell(b: {
  account: Account | null;
  sessionIdentity: WasmIdentity | null;
  relayStatus: string;
  relayClient?: RelayPool | null;
  query: string;
  setQuery: (v: string) => void;
  conversations: Conversation[];
  filteredConversations: Conversation[];
  contacts: Contact[];
  activeId: string | null;
  activeConv: Conversation | null;
  activeContact: Contact | null;
  activeGroup: GroupRecord | null;
  messages: MessageEvent[];
  unreadAtOpen?: number;
  searchMessages?: (q: string) => Promise<MessageHit[]>;
  loadStarred?: (conversationId: string | null) => Promise<MessageHit[]>;
  chatQuery: string;
  setChatQuery: (v: string) => void;
  draft: string;
  setDraft: (v: string) => void;
  replyTo: MessageEvent | null;
  editing: MessageEvent | null;
  setReplyTo: (m: MessageEvent | null) => void;
  setEditing: (m: MessageEvent | null) => void;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  voiceUrlFor: (id: string) => string | undefined;
  showDetails: boolean;
  setShowDetails: (value: boolean | ((v: boolean) => boolean)) => void;
  showAdd: boolean;
  setShowAdd: (v: boolean) => void;
  showCreateGroup: boolean;
  setShowCreateGroup: (v: boolean) => void;
  showSettings: boolean;
  setShowSettings: (v: boolean) => void;
  settingsTab: SettingsTab;
  setSettingsTab: (t: SettingsTab) => void;
  unverifiedCount: number;
  openSecurityCount: number;
  securityEvents: SecurityEvent[];
  vaultCaps: SecureVaultCapabilities | null;
  notificationPrefs: NotificationPrefs;
  setNotificationPrefs: (p: NotificationPrefs) => void;
  saveNotificationPrefs: (p: NotificationPrefs) => void;
  notificationPermission: NotificationPermissionState;
  setNotificationPermission: (p: NotificationPermissionState) => void;
  requestNotificationPermission: () => Promise<NotificationPermissionState>;
  onLockNow?: () => void;
  banner: InAppBannerPayload | null;
  setBanner: (b: InAppBannerPayload | null) => void;
  toast: string | null;
  setToast: (t: string | null) => void;
  openConversation: (id: string) => Promise<void>;
  addContact: (
    name: string,
    hybrid: string,
    messaging: string,
    transport?: string,
  ) => Promise<void>;
  chat: ChatActions;
  groups: GroupActions;
  refresh: () => Promise<void>;
  /** Decrypted media held in memory this session; clearing returns how many were dropped. */
  mediaCacheCount?: number;
  onClearMediaCache?: () => number;
  setActiveId: (id: string | null) => void;
  disappearTimer: DisappearTimerSec;
  groupForkWarning: string | null;
  onTogglePin: () => void;
  onToggleMute: () => void;
  onArchive: () => void;
  onBlock: () => void;
  onAcceptRequest?: () => void;
  onDeclineRequest?: (block: boolean) => void;
  onAcceptGroupInvite?: () => void;
  onReportBlock?: (contact: Contact, fromRequest: boolean) => Promise<void>;
  onDeclineGroupInvite?: (block: boolean) => void;
  onOpenContact?: (contactId: string) => void;
  onDisappearChange: (sec: DisappearTimerSec) => void;
  onToggleChatLock: () => void;
  onClearActiveMessages: () => void;
  onDeleteActiveChat: () => void;
  onCreatePoll?: () => void;
  onVotePoll?: (m: MessageEvent, optionId: string) => void;
  showPollModal?: boolean;
  setShowPollModal?: (v: boolean) => void;
  onSubmitPoll?: (q: string, opts: string[]) => void;
  pendingChatLock?: import("@veto/domain").ChatLockRecord | null;
  onChatLockUnlock?: () => void;
  onChatLockCancel?: () => void;
  /** Conversation id awaiting a PIN for its new chat lock (modal open). */
  chatLockPromptFor?: string | null;
  onChatLockPromptClose?: () => void;
  onChatLockPromptConfirm?: (pin: string) => void;
  onChatRowAction: (
    conv: Conversation,
    contact: Contact | null,
    action: ChatRowAction,
    value?: DisappearTimerSec,
  ) => void;
  onChatLockedCheck: (conversationId: string) => Promise<boolean>;
  onChatDisappearGet: (conversationId: string) => Promise<DisappearTimerSec>;
  onSaveProfile: (input: ProfileInput) => Promise<void>;
}) {
  return (
    <>
    <AppShell
      account={b.account}
      sessionIdentity={b.sessionIdentity}
      relayStatus={b.relayStatus}
        relayClient={b.relayClient}
      query={b.query}
      setQuery={b.setQuery}
      conversations={b.conversations}
      filteredConversations={b.filteredConversations}
      contacts={b.contacts}
      activeId={b.activeId}
      activeConv={b.activeConv}
      activeContact={b.activeContact}
      activeGroup={b.activeGroup}
      messages={b.messages}
      unreadAtOpen={b.unreadAtOpen}
      searchMessages={b.searchMessages}
      loadStarred={b.loadStarred}
      chatQuery={b.chatQuery}
      setChatQuery={b.setChatQuery}
      draft={b.draft}
      setDraft={b.setDraft}
      replyTo={b.replyTo}
      editing={b.editing}
      setReplyTo={b.setReplyTo}
      setEditing={b.setEditing}
      messagesEndRef={b.messagesEndRef}
      voiceUrlFor={b.voiceUrlFor}
      showDetails={b.showDetails}
      setShowDetails={b.setShowDetails}
      showAdd={b.showAdd}
      setShowAdd={b.setShowAdd}
      showCreateGroup={b.showCreateGroup}
      setShowCreateGroup={b.setShowCreateGroup}
      showSettings={b.showSettings}
      setShowSettings={b.setShowSettings}
      settingsTab={b.settingsTab}
      setSettingsTab={b.setSettingsTab}
      unverifiedCount={b.unverifiedCount}
      openSecurityCount={b.openSecurityCount}
      securityEvents={b.securityEvents}
      vaultCaps={b.vaultCaps}
      notificationPrefs={b.notificationPrefs}
      setNotificationPrefs={b.setNotificationPrefs}
      saveNotificationPrefs={b.saveNotificationPrefs}
      notificationPermission={b.notificationPermission}
      setNotificationPermission={b.setNotificationPermission}
      requestNotificationPermission={b.requestNotificationPermission}
      onLockNow={b.onLockNow}
      refresh={b.refresh}
      mediaCacheCount={b.mediaCacheCount}
      onClearMediaCache={b.onClearMediaCache}
      banner={b.banner}
      setBanner={b.setBanner}
      toast={b.toast}
      setToast={b.setToast}
      onOpenConversation={(id) => void b.openConversation(id)}
      onSend={() => void b.chat.sendMessage()}
      onAttachImage={(f) => void b.chat.sendImage(f)}
      onAttachDocument={(f) => void b.chat.sendDocument(f)}
      onViewOnceImage={(f) => void b.chat.sendViewOnceImage(f)}
      onSendSticker={(pack, id) => void b.chat.sendSticker(pack, id)}
      onSendStickerFile={(f) => void b.chat.sendStickerFile(f)}
      onSendGifFile={(f) => void b.chat.sendGif(f)}
      onVoice={(blob, ms) => void b.chat.sendVoiceNote(blob, ms)}
      onVoiceError={(message) => b.setToast(message)}
      onDirect={() => void b.chat.tryOpenDirectPeer()}
      onReply={(m) => {
        b.setReplyTo(m);
        b.setEditing(null);
      }}
      onReact={(m, emoji) => void b.chat.sendReaction(m, emoji)}
      onEdit={(m) => {
        b.setEditing(m);
        b.setDraft(m.body);
        b.setReplyTo(null);
      }}
      onDeleteMe={(m) => void b.chat.deleteForMe(m)}
      onDeleteEveryone={(m) => void b.chat.deleteForEveryone(m)}
      onForward={(m) => void b.chat.forwardMessage(m)}
      onForwardTo={(m, targets) => void b.chat.forwardTo(m, targets)}
      onReportBlock={b.onReportBlock}
      onStar={(m) => void b.chat.starMessage(m)}
      onPin={(m) => void b.chat.pinMessage(m)}
      onOpenViewOnce={(m) => void b.chat.openViewOnce(m)}
      onCreatePoll={b.onCreatePoll}
      onVotePoll={b.onVotePoll}
      onAddContact={b.addContact}
      onCreateGroup={b.groups.createGroup}
      onSaveProfile={b.onSaveProfile}
      onVerifyContact={() => {
        if (!b.activeContact) return;
        const verified = { ...b.activeContact, verificationState: "verified" as const };
        void (async () => {
          await saveContact(verified);
          // Clear a stale key-change warning preview — the alert did its job,
          // the contact re-verified (FR-CON-003 recovery path).
          if (b.activeConv?.lastMessagePreview.startsWith("⚠ Key change")) {
            await upsertConversation({
              ...b.activeConv,
              lastMessagePreview: "Chat ready — safety improved",
            });
          }
          await b.refresh();
          b.setToast("Verified — safety improved");
        })();
      }}
      onRemoveGroupMember={(hex) => void b.groups.removeGroupMember(hex)}
      onSetGroupRole={(hex, role) => void b.groups.changeGroupRole(hex, role)}
      onAddGroupMembers={(ids) => void b.groups.addGroupMembers(ids)}
      onRenameGroup={(title) => void b.groups.renameGroup(title)}
      onBack={() => b.setActiveId(null)}
      onChatRowAction={b.onChatRowAction}
      onChatLockedCheck={b.onChatLockedCheck}
      onChatDisappearGet={b.onChatDisappearGet}
      disappearTimer={b.disappearTimer}
      groupForkWarning={b.groupForkWarning}
      onTogglePin={b.onTogglePin}
      onToggleMute={b.onToggleMute}
      onArchive={b.onArchive}
      onBlock={b.onBlock}
      onAcceptRequest={b.onAcceptRequest}
      onDeclineRequest={b.onDeclineRequest}
      onAcceptGroupInvite={b.onAcceptGroupInvite}
      onDeclineGroupInvite={b.onDeclineGroupInvite}
      onOpenContact={b.onOpenContact}
      onDisappearChange={b.onDisappearChange}
      onToggleChatLock={b.onToggleChatLock}
      onClearActiveMessages={b.onClearActiveMessages}
      onDeleteActiveChat={b.onDeleteActiveChat}
    />
      {b.showPollModal && b.setShowPollModal && b.onSubmitPoll && (
        <CreatePollModal
          onClose={() => b.setShowPollModal!(false)}
          onCreate={(q, o) => {
            b.onSubmitPoll!(q, o);
            b.setShowPollModal!(false);
          }}
        />
      )}
      {b.pendingChatLock && b.onChatLockUnlock && b.onChatLockCancel && (
        <ChatLockGate
          lock={b.pendingChatLock}
          title={b.activeConv?.title ?? "Chat"}
          onUnlock={b.onChatLockUnlock}
          onCancel={b.onChatLockCancel}
        />
      )}
      {b.chatLockPromptFor && b.onChatLockPromptClose && b.onChatLockPromptConfirm && (
        <ChatLockPromptModal
          title={b.conversations.find((c) => c.id === b.chatLockPromptFor)?.title ?? "chat"}
          onClose={b.onChatLockPromptClose}
          onConfirm={b.onChatLockPromptConfirm}
        />
      )}
    </>
  );
}
